/**
 * `defer-templates-user-saved` + `defer-templates-ai-generated` —
 * request / response shapes for the Template gallery's two
 * long-deferred entry points:
 *
 *   * **User-saved** — "Save as template…" from an open flow
 *     persists the template under the active org. The gallery
 *     then surfaces it next to the curated registry.
 *   * **AI-generated** — `POST /api/templates/generate` takes a
 *     free-form prompt and returns a `FlowTemplate` shape ready
 *     to instantiate or save.
 *
 * The two finishers share one persistence row (`flow_templates`)
 * and one JSON payload shape (`UserFlowTemplatePayload`) so the
 * gallery doesn't have to learn three storage backends. The
 * `source` discriminator (`'user'` vs `'ai'`) lets the UI badge
 * each row and lets governance lists ("templates I authored",
 * "templates minted by an LLM") work without an extra join.
 *
 * Authoring note: this file is intentionally **decoupled** from
 * the editor-only `FlowTemplate` interface in
 * `packages/flow-builder/src/lib/templates/types.ts`. The two
 * shapes are kept structurally compatible — both carry
 * `id / name / description / mode / category / tags / icon /
 * difficulty / steps[]` — but the editor file owns the editor
 * concerns (`StepType` import, `TestMode` import) while this
 * contract owns the cross-boundary wire shape.
 */

import { z } from 'zod';

export const TemplateCategorySchema = z.enum(['web', 'api', 'fullstack', 'data', 'visual', 'auth']);
export type TemplateCategoryWire = z.infer<typeof TemplateCategorySchema>;

export const TemplateDifficultySchema = z.enum(['beginner', 'intermediate', 'advanced']);
export type TemplateDifficultyWire = z.infer<typeof TemplateDifficultySchema>;

export const TemplateModeSchema = z.enum(['web', 'api', 'fullstack']);
export type TemplateModeWire = z.infer<typeof TemplateModeSchema>;

/**
 * One step inside a `FlowTemplate`. We deliberately validate the
 * shape (string fields, no unknown keys) but NOT the `type` field
 * against the live `StepType` union — step types evolve faster
 * than the wire schema and we'd rather accept a future step type
 * we don't yet know about than reject a valid template.
 */
export const TemplateStepPayloadSchema = z.object({
  type: z.string().min(1).max(64),
  title: z.string().max(200).optional(),
  target: z.string().max(2048).optional(),
  value: z.string().max(8192).optional(),
  role: z.string().max(64).optional(),
  fields: z.record(z.string(), z.string().max(8192)).optional(),
});
export type TemplateStepPayload = z.infer<typeof TemplateStepPayloadSchema>;

/**
 * The portable `FlowTemplate` shape we send across the wire.
 * Mirrors the editor-local interface (see above) without
 * importing `StepType` or `TestMode` from the editor package
 * (frontend can't depend on backend).
 */
export const UserFlowTemplatePayloadSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().min(1).max(160),
  description: z.string().max(2048).default(''),
  mode: TemplateModeSchema,
  category: TemplateCategorySchema,
  tags: z.array(z.string().min(1).max(64)).max(20).default([]),
  icon: z.string().min(1).max(64).default('FileText'),
  difficulty: TemplateDifficultySchema,
  steps: z.array(TemplateStepPayloadSchema).min(1).max(50),
});
export type UserFlowTemplatePayload = z.infer<typeof UserFlowTemplatePayloadSchema>;

export const FlowTemplateSourceSchema = z.enum(['user', 'ai']);
export type FlowTemplateSource = z.infer<typeof FlowTemplateSourceSchema>;

/**
 * Read-side projection used by the gallery + admin UIs.
 */
export const FlowTemplateRowSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1).max(128),
  name: z.string().min(1).max(160),
  description: z.string().max(2048),
  source: FlowTemplateSourceSchema,
  authorUserId: z.string().uuid().nullable(),
  payload: UserFlowTemplatePayloadSchema,
  aiPrompt: z.string().max(8192).nullable(),
  aiProvider: z.string().max(64).nullable(),
  aiModel: z.string().max(128).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FlowTemplateRow = z.infer<typeof FlowTemplateRowSchema>;

/** GET /api/templates */
export const ListFlowTemplatesResponseSchema = z.object({
  templates: z.array(FlowTemplateRowSchema),
});
export type ListFlowTemplatesResponse = z.infer<typeof ListFlowTemplatesResponseSchema>;

/** POST /api/templates — create-or-update by slug. */
export const SaveFlowTemplateRequestSchema = z.object({
  /**
   * Optional client-supplied slug. When omitted, the server
   * derives one from `payload.name` (`kebab-case-of-name`) and
   * disambiguates collisions with a numeric suffix.
   */
  slug: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/, {
      message: 'Slug must be lowercase kebab-case',
    })
    .optional(),
  source: FlowTemplateSourceSchema.default('user'),
  payload: UserFlowTemplatePayloadSchema,
  aiPrompt: z.string().max(8192).optional(),
  aiProvider: z.string().max(64).optional(),
  aiModel: z.string().max(128).optional(),
});
export type SaveFlowTemplateRequest = z.infer<typeof SaveFlowTemplateRequestSchema>;

/** POST /api/templates response. */
export const SaveFlowTemplateResponseSchema = z.object({
  template: FlowTemplateRowSchema,
});
export type SaveFlowTemplateResponse = z.infer<typeof SaveFlowTemplateResponseSchema>;

/** DELETE /api/templates/:id response. */
export const DeleteFlowTemplateResponseSchema = z.object({
  deleted: z.boolean(),
});
export type DeleteFlowTemplateResponse = z.infer<typeof DeleteFlowTemplateResponseSchema>;

// ---------------------------------------------------------------------------
// AI generator endpoint
// ---------------------------------------------------------------------------

/** POST /api/templates/generate — body. */
export const GenerateFlowTemplateRequestSchema = z.object({
  /** The user's free-form description of the desired flow. */
  prompt: z.string().min(8).max(2000),
  /**
   * Optional hint nudging the LLM toward a specific category.
   * The server still validates the response is one of the
   * allowed categories so even a non-compliant hint stays safe.
   */
  categoryHint: TemplateCategorySchema.optional(),
  /** Optional baseUrl seed for templated step values. */
  baseUrl: z.string().max(2048).optional(),
});
export type GenerateFlowTemplateRequest = z.infer<typeof GenerateFlowTemplateRequestSchema>;

/** POST /api/templates/generate — response. */
export const GenerateFlowTemplateResponseSchema = z.object({
  /** The generated template, ready to save or instantiate. */
  template: UserFlowTemplatePayloadSchema,
  /** Provider that fulfilled the call (`mock` when deterministic fallback fired). */
  provider: z.string().min(1).max(64),
  /** Model identifier the provider returned (or `'rynora-deterministic'`). */
  model: z.string().min(1).max(128),
  /** True when the deterministic fallback was used because no AI is configured. */
  fallback: z.boolean(),
});
export type GenerateFlowTemplateResponse = z.infer<typeof GenerateFlowTemplateResponseSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive a stable slug from a template name. Lowercase,
 * kebab-case, alphanumeric-only, length-capped. Used server-side
 * when the client omits an explicit slug on save.
 */
export function deriveTemplateSlug(name: string): string {
  const stripped = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!stripped) return 'untitled-template';
  return stripped.slice(0, 80);
}
